import React, { useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { App, Button, Form, List, Row, Col, Typography, Spin } from 'antd';
import { Navigate } from 'react-router';
import CardLayout from '../../layouts/CardLayout';
import { ContractorForm } from '../../components/Contractor/ContractorForm';
import {
  CONTRACTOR_MEMBERS_UPDATE,
  CONTRACTOR_UPDATE
} from '../../graphql/mutations/contractors';
import { MY_CONTRACTOR } from '../../graphql/queries/contractors';
import { useAuth } from '../../providers/AuthProvider';
import { ContractorFormValues } from '../../types/contractor';
import { ContractorInfoCard } from '../../components/Contractor/ContractorInfoCard';

const { Title, Paragraph } = Typography;

export const ContractorEdit: React.FC = () => {
  const { user, setAuthUser } = useAuth();
  const { message } = App.useApp();
  const [form] = Form.useForm<ContractorFormValues>();

  const {
    data,
    loading: loadingContractor,
    refetch,
  } = useQuery(MY_CONTRACTOR, {
    skip: !user?.roles?.includes('CONTRACTOR'),
    fetchPolicy: 'network-only',
  });

  const contractor = data?.myContractor;
  const isOwner =
    contractor?.ownerUserId && user?.id && contractor.ownerUserId === user.id;

  useEffect(() => {
    if (contractor && user && user.contractor?.id !== contractor.id) {
      setAuthUser({ ...user, contractor });
    }
  }, [contractor, setAuthUser, user]);

  const [updateContractor, { loading }] = useMutation(CONTRACTOR_UPDATE, {
    onCompleted: result => {
      const updated = result?.contractorUpdate;
      if (updated && user) {
        message.success('Contractor profile updated');
        setAuthUser({ ...user, contractor: updated });
        refetch();
      }
    },
    onError: error => {
      message.error(error.message || 'Failed to update contractor profile');
    },
  });

  const [updateMembers, { loading: updatingMembers }] = useMutation(CONTRACTOR_MEMBERS_UPDATE, {
    onCompleted: result => {
      if (result?.contractorMembersUpdate) {
        message.success('Members updated');
        refetch();
      }
    },
    onError: error => {
      message.error(error.message || 'Failed to update members');
    },
  });

  if (!user?.roles?.includes('CONTRACTOR')) {
    return <Navigate to="/" replace />;
  }

  if (loadingContractor) {
    return (
      <Row justify="center" style={{ width: '100%' }}>
        <Col>
          <Spin size="large" />
        </Col>
      </Row>
    );
  }

  if (!contractor) {
    return <Navigate to="/contractor/setup" replace />;
  }

  if (!isOwner) {
    return (
      <Row justify="center" style={{ width: '100%', margin: '1em' }}>
        <Col xs={24} sm={20} md={14} lg={10}>
          <CardLayout>
            <Title level={3}>Contractor Info</Title>
            <Paragraph>Company details and available area.</Paragraph>
            <ContractorInfoCard contractor={contractor} isContractorUser={true} />
          </CardLayout>
        </Col>
      </Row>
    );
  }

  const handleFinish = (values: ContractorFormValues) => {
    updateContractor({
      variables: {
        id: contractor.id,
        input: {
          name: values.name,
          phone: values.phone,
          email: values.email,
          description: values.description,
          availableArea: values.availableArea,
        },
      },
    });
  };

  const handleRemoveMember = (memberId: string) => {
    updateMembers({
      variables: {
        id: contractor.id,
        removeUserIds: [memberId],
      },
    });
  };

  return (
    <Row justify="center" style={{ width: '100%', margin: '1em' }}>
      <Col xs={24} sm={20} md={14} lg={10}>
        <CardLayout>
          <Title level={3}>Edit Contractor Info</Title>
          <Paragraph>Keep your contractor details up to date.</Paragraph>
          <ContractorForm
            form={form}
            loading={loading}
            onFinish={handleFinish}
            submitText="Update"
            initialValues={contractor}
          />
          <div style={{ marginTop: 24 }}>
            <Title level={4}>Members</Title>
            <List
              dataSource={contractor.members || []}
              locale={{ emptyText: 'No members found' }}
              renderItem={(member: { id: string; name: string; email: string }) => {
                const isOwnerMember = contractor.ownerUserId === member.id;
                return (
                  <List.Item
                    actions={[
                      <Button
                        key="remove"
                        danger
                        size="small"
                        disabled={isOwnerMember}
                        onClick={() => handleRemoveMember(member.id)}
                        loading={updatingMembers}>
                        Remove
                      </Button>,
                    ]}>
                    <List.Item.Meta title={member.name} description={member.email} />
                  </List.Item>
                );
              }}
            />
          </div>
        </CardLayout>
      </Col>
    </Row>
  );
};
